import API_CONFIG from '../config/api';

export class ApiError extends Error {
  constructor(
    value: string,
    public status?: number,
    public code?: string
  ) {
    super(value);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private baseURL: string;
  private timeout: number;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  setBaseURL(url: string) {
    this.baseURL = url;
    if (API_CONFIG.DEBUG) {
      console.log(`API base URL updated to: ${url}`);
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('doyenToken');
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`;
    }
    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);
      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        // Read raw text first to avoid losing error content
        const text = await response.body?.getReader().read().then(({ value }) => {
          const decoder = new TextDecoder('utf-8');
          return decoder.decode(value);
        }).then((decoded) => decoded.toString());
        
        throw new ApiError(
          text || 'API request failed',
          response.status,
          response.statusText
        );
      }
      return response.json();
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (error instanceof Error) {
        if (error.name === 'AbortError') throw new ApiError('Request timeout');
        throw new ApiError(error.message);
      }
      throw new ApiError('Unknown error occurred');
    }
  }

  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }
  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
  put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
  patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const apiClient = new ApiClient();
